import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useState, useMemo } from "react";

/**
 * Reusable drill-down dialog for dashboard widgets.
 *
 * Props:
 *   open       - boolean
 *   onClose    - () => void
 *   title      - string (e.g. "Payroll Details")
 *   subtitle   - string (e.g. "Branch: UNIT 1 | March 2026")
 *   columns    - [{ key, label, align?, format?, width? }]
 *   rows       - array of objects
 *   loading    - boolean
 */
export default function DrillDownDialog({
  open,
  onClose,
  title = "",
  subtitle = "",
  columns = [],
  rows = [],
  loading = false,
}) {
  const [search, setSearch] = useState("");

  // Reset search when dialog closes
  const handleClose = () => {
    setSearch("");
    onClose();
  };

  // Filter rows by search across all string fields
  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      columns.some((c) => {
        const val = r[c.key];
        return val != null && String(val).toLowerCase().includes(q);
      })
    );
  }, [rows, search, columns]);

  const cellSx = {
    fontSize: "13px",
    whiteSpace: "nowrap",
    py: 1.2,
  };

  const headerSx = {
    fontWeight: "bold",
    backgroundColor: "#f5f3ff",
    whiteSpace: "nowrap",
    fontSize: "13px",
    py: 1.2,
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#4A00E0",
          fontWeight: "bold",
          pb: 0,
        }}
      >
        {title}
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Subtitle + Search */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            mt: 1,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" color="textSecondary">
              {subtitle}
            </Typography>
            {!loading && rows.length > 0 && (
              <Chip
                label={`${filtered.length} record${filtered.length !== 1 ? "s" : ""}`}
                size="small"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
          {!loading && rows.length > 5 && (
            <TextField
              size="small"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>

        {/* Loading */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Empty */}
        {!loading && rows.length === 0 && (
          <Typography textAlign="center" color="textSecondary" sx={{ py: 4 }}>
            No data available.
          </Typography>
        )}

        {/* Table */}
        {!loading && rows.length > 0 && (
          <TableContainer
            component={Paper}
            sx={{ borderRadius: 2, maxHeight: 480, overflowX: "auto" }}
          >
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  <TableCell sx={{ ...headerSx, width: 50 }}>#</TableCell>
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align || "left"}
                      sx={{
                        ...headerSx,
                        ...(col.width ? { width: col.width } : {}),
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((row, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ ...cellSx, width: 50, color: "#999" }}>
                      {idx + 1}
                    </TableCell>
                    {columns.map((col) => {
                      const val = row[col.key];
                      const display = col.format ? col.format(val, row) : val;

                      // Render status as a colored chip
                      if (col.key === "status") {
                        return (
                          <TableCell
                            key={col.key}
                            align={col.align || "left"}
                            sx={cellSx}
                          >
                            <Chip
                              label={display}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                backgroundColor:
                                  String(display).toLowerCase() === "present"
                                    ? "#e8f5e9"
                                    : "#ffebee",
                                color:
                                  String(display).toLowerCase() === "present"
                                    ? "#2e7d32"
                                    : "#c62828",
                              }}
                            />
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell
                          key={col.key}
                          align={col.align || "left"}
                          sx={cellSx}
                        >
                          {display ?? "-"}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
                {filtered.length === 0 && search && (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 1}
                      align="center"
                      sx={{ py: 3 }}
                    >
                      <Typography color="textSecondary">
                        No matches for &quot;{search}&quot;
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
}
